/**
 * Tax Classification Types
 * 
 * Shared type definitions for tax classification across frontend and backend.
 */

// Import symbol classification from centralized config
import {
  isTreasurySymbol,
  isMunicipalSymbol,
  isReitSymbol,
  isMlpSymbol,
  isMoneyMarketSymbol
} from '../config/symbols/index.js';

// Import TaxCategory from enums (moved there for consistency)
import { TaxCategory } from '../enums/TaxCategory.js';

/**
 * Tax classification metadata for a category
 */
export interface TaxClassification {
  category: TaxCategory;
  federalTaxable: boolean;
  stateTaxable: boolean;
  preferentialRate: boolean;  // Gets long-term capital gains rate
  ficaEligible?: boolean;     // Subject to FICA (Social Security + Medicare)
  selfEmploymentTaxEligible?: boolean;  // Subject to self-employment tax
  niitEligible?: boolean;     // Subject to Net Investment Income Tax (3.8%)
  description: string;
}

/**
 * Tax classification definitions - the full lookup table
 */
export const TAX_CLASSIFICATIONS: Record<TaxCategory, TaxClassification> = {
  [TaxCategory.QualifiedDividend]: {
    category: TaxCategory.QualifiedDividend,
    federalTaxable: true,
    stateTaxable: true,
    preferentialRate: true,
    niitEligible: true,
    description: 'Qualified Dividend'
  },
  [TaxCategory.OrdinaryDividend]: {
    category: TaxCategory.OrdinaryDividend,
    federalTaxable: true,
    stateTaxable: true,
    preferentialRate: false,
    niitEligible: true,
    description: 'Ordinary Dividend'
  },
  [TaxCategory.Interest]: {
    category: TaxCategory.Interest,
    federalTaxable: true,
    stateTaxable: true,
    preferentialRate: false,
    niitEligible: true,
    description: 'Interest Income'
  },
  [TaxCategory.TreasuryInterest]: {
    category: TaxCategory.TreasuryInterest,
    federalTaxable: true,
    stateTaxable: false,
    preferentialRate: false,
    niitEligible: true,
    description: 'Treasury Interest'
  },
  [TaxCategory.MunicipalInterest]: {
    category: TaxCategory.MunicipalInterest,
    federalTaxable: false,
    stateTaxable: true,  // Most states tax out-of-state municipal bonds
    preferentialRate: false,
    niitEligible: false,
    description: 'Municipal Bond Interest'
  },
  [TaxCategory.CapitalGainShort]: {
    category: TaxCategory.CapitalGainShort,
    federalTaxable: true,
    stateTaxable: true,
    preferentialRate: false,
    niitEligible: true,
    description: 'Short-Term Capital Gain'
  },
  [TaxCategory.CapitalGainLong]: {
    category: TaxCategory.CapitalGainLong,
    federalTaxable: true,
    stateTaxable: true,
    preferentialRate: true,
    niitEligible: true,
    description: 'Long-Term Capital Gain'
  },
  [TaxCategory.OptionPremium]: {
    category: TaxCategory.OptionPremium,
    federalTaxable: true,
    stateTaxable: true,
    preferentialRate: false,
    niitEligible: true,
    description: 'Option Premium'
  },
  [TaxCategory.ReturnOfCapital]: {
    category: TaxCategory.ReturnOfCapital,
    federalTaxable: false,
    stateTaxable: false,
    preferentialRate: false,
    niitEligible: false,
    description: 'Return of Capital'
  },
  [TaxCategory.W2Wages]: {
    category: TaxCategory.W2Wages,
    federalTaxable: true,
    stateTaxable: true,
    preferentialRate: false,
    ficaEligible: true,
    niitEligible: false,
    description: 'W-2 Wages & Salary'
  },
  [TaxCategory.SelfEmploymentIncome]: {
    category: TaxCategory.SelfEmploymentIncome,
    federalTaxable: true,
    stateTaxable: true,
    preferentialRate: false,
    selfEmploymentTaxEligible: true,
    niitEligible: false,
    description: 'Self-Employment Income (1099)'
  },
  [TaxCategory.PrivateStockGainLong]: {
    category: TaxCategory.PrivateStockGainLong,
    federalTaxable: true,
    stateTaxable: true,
    preferentialRate: true,
    niitEligible: true,
    description: 'External Capital Gains (Long-Term)'
  },
  [TaxCategory.PrivateStockGainShort]: {
    category: TaxCategory.PrivateStockGainShort,
    federalTaxable: true,
    stateTaxable: true,
    preferentialRate: false,
    niitEligible: true,
    description: 'External Capital Gains (Short-Term)'
  },
  [TaxCategory.PassiveIncome]: {
    category: TaxCategory.PassiveIncome,
    federalTaxable: true,
    stateTaxable: true,
    preferentialRate: false,
    niitEligible: true, // Passive rental/K-1 income is NII (Form 8960 line 4a); non-passive trade-or-business rental is out of scope (TAX-06)
    description: 'Passive Income (Rental, K-1, etc.)'
  },
  [TaxCategory.IsoExerciseSpread]: {
    category: TaxCategory.IsoExerciseSpread,
    federalTaxable: false,
    stateTaxable: false,
    preferentialRate: false,
    niitEligible: false,
    description: 'ISO Exercise Spread (AMT Only)'
  },
  [TaxCategory.Other]: {
    category: TaxCategory.Other,
    federalTaxable: true,
    stateTaxable: true,
    preferentialRate: false,
    niitEligible: false,
    description: 'Other Income'
  }
};

/**
 * Get display label for a tax category
 */
/**
 * Get all tax categories
 */
export function getAllTaxCategories(): TaxCategory[] {
  return Object.keys(TAX_CLASSIFICATIONS) as TaxCategory[];
}

// ============================================================================
// Tax Classification Logic
// ============================================================================

// Symbol classification is now handled by config/symbols module
// See: isTreasurySymbol, isMunicipalSymbol, isReitSymbol, isMlpSymbol

/**
 * Dividend description patterns
 */
const QUALIFIED_DIVIDEND_PATTERNS = [/qualified/i, /qual\.?\s*div/i];
const ORDINARY_DIVIDEND_PATTERNS = [/non.?qual/i, /ordinary/i, /short.?term/i, /reit/i, /mlp/i];
const RETURN_OF_CAPITAL_PATTERNS = [/return of capital/i, /roc/i, /non.?dividend distribution/i];

/**
 * Classify a dividend or interest transaction for tax purposes
 *
 * @param symbol - Security symbol (e.g., 'AAPL', 'VNQ')
 * @param description - Transaction description
 * @param incomeType - Type of income ('dividend', 'interest', etc.)
 * @param _amount - Transaction amount (unused, for future use)
 * @returns The tax category for this income
 */
export function classifyForTax(
  symbol: string | undefined,
  description: string | undefined,
  incomeType: string,
  _amount: number
): TaxCategory {
  const sym = symbol?.toUpperCase() || '';
  const desc = description || '';

  // Handle interest income
  if (incomeType === 'interest') {
    if (isTreasurySymbol(sym) || /treasury|t-bill|tbill|t-bond|t-note|us\s*government/i.test(desc)) {
      return TaxCategory.TreasuryInterest;
    }
    if (isMunicipalSymbol(sym) || /municipal|muni\s*bond|tax.?exempt|tax.?free/i.test(desc)) {
      return TaxCategory.MunicipalInterest;
    }
    return TaxCategory.Interest;
  }

  // Handle dividends
  if (incomeType === 'dividend' || incomeType === 'reinvested_dividend' ||
      incomeType === 'ordinary_dividend' || incomeType === 'qualified_dividend') {
    // If already classified, use it directly
    if (incomeType === 'ordinary_dividend') return TaxCategory.OrdinaryDividend;
    if (incomeType === 'qualified_dividend') return TaxCategory.QualifiedDividend;

    // Check for return of capital
    if (RETURN_OF_CAPITAL_PATTERNS.some(p => p.test(desc))) {
      return TaxCategory.ReturnOfCapital;
    }

    // Check for municipal fund dividends
    if (isMunicipalSymbol(sym) || /municipal|muni\s*bond|tax.?exempt|tax.?free/i.test(desc)) {
      return TaxCategory.MunicipalInterest;
    }

    // Check for treasury fund dividends
    if (isTreasurySymbol(sym) || /treasury|t-bill|tbill|t-bond|t-note|us\s*government/i.test(desc)) {
      return TaxCategory.TreasuryInterest;
    }

    // Money market fund distributions are paid as dividends but are
    // interest-like and never qualified (non-treasury MMFs land here)
    if (isMoneyMarketSymbol(sym, desc)) {
      return TaxCategory.Interest;
    }

    // Check for REIT symbols (dividends are ordinary income)
    // Also check description for "REIT" keyword to catch individual REITs
    if (isReitSymbol(sym) || /\bREIT\b/i.test(desc)) {
      return TaxCategory.OrdinaryDividend;
    }

    // Check for MLP symbols (dividends are ordinary income)
    if (isMlpSymbol(sym)) {
      return TaxCategory.OrdinaryDividend;
    }

    // Check description for explicit qualified/non-qualified
    if (QUALIFIED_DIVIDEND_PATTERNS.some(p => p.test(desc))) {
      return TaxCategory.QualifiedDividend;
    }

    if (ORDINARY_DIVIDEND_PATTERNS.some(p => p.test(desc))) {
      return TaxCategory.OrdinaryDividend;
    }

    // Default: most US stock dividends are qualified
    return TaxCategory.QualifiedDividend;
  }

  // Handle option premiums
  if (incomeType === 'option_premium') return TaxCategory.OptionPremium;

  // Handle capital gains
  if (incomeType === 'capital_gain_short') return TaxCategory.CapitalGainShort;
  if (incomeType === 'capital_gain_long') return TaxCategory.CapitalGainLong;

  return TaxCategory.Other;
}
